import "@/App.css";
import "@/modules/index";  // side-effect: registers all frontend modules (lazy)
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider } from "@/lib/auth";
import { FeatureVisibilityProvider } from "@/lib/featureVisibility";
import { ModuleProvider, frontendModules } from "@/core/ModuleRegistry";
import { Toaster } from "sonner";

// Critical path (every cold load eventually renders one of these) — kept eager.
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import RootRouter from "@/pages/RootRouter";

// Everything else is route-split. First visit no longer downloads the entire
// SuperAdmin console, every /app sub-page, and every module to show the
// landing page.
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AppShell = lazy(() => import("@/pages/app/AppShell"));
const DashboardRouter = lazy(() => import("@/pages/app/DashboardRouter"));
const MarketplacePage = lazy(() => import("@/pages/app/MarketplacePage"));
const MarketplaceGuard = lazy(() => import("@/pages/app/MarketplaceGuard"));
const ProductsPage = lazy(() => import("@/pages/app/ProductsPage"));
const SettingsPage = lazy(() => import("@/pages/app/SettingsPage"));
const BillingPage = lazy(() => import("@/pages/app/BillingPage"));
const BillingSuccessPage = lazy(() => import("@/pages/app/BillingSuccessPage"));
const TemplatesPage = lazy(() => import("@/pages/app/TemplatesPage"));
const CompanySettingsPage = lazy(() => import("@/pages/app/CompanySettingsPage"));
const SuperAdminLoginPage = lazy(() => import("@/pages/superadmin/SuperAdminLoginPage"));
const SuperAdminShell = lazy(() => import("@/pages/superadmin/SuperAdminShell"));
const SuperAdminOverview = lazy(() => import("@/pages/superadmin/SuperAdminOverview"));
const TenantsListPage = lazy(() => import("@/pages/superadmin/TenantsPages").then(m => ({ default: m.TenantsListPage })));
const TenantDetailPage = lazy(() => import("@/pages/superadmin/TenantsPages").then(m => ({ default: m.TenantDetailPage })));
const PlansManagerPage = lazy(() => import("@/pages/superadmin/SuperAdminManagers").then(m => ({ default: m.PlansManagerPage })));
const ModulesManagerPage = lazy(() => import("@/pages/superadmin/SuperAdminManagers").then(m => ({ default: m.ModulesManagerPage })));
const CouponsManagerPage = lazy(() => import("@/pages/superadmin/SuperAdminManagers").then(m => ({ default: m.CouponsManagerPage })));
const TransactionsPage = lazy(() => import("@/pages/superadmin/SuperAdminManagers").then(m => ({ default: m.TransactionsPage })));
const AuditPage = lazy(() => import("@/pages/superadmin/SuperAdminManagers").then(m => ({ default: m.AuditPage })));
const GatewaysManagerPage = lazy(() => import("@/pages/superadmin/GatewaysManagerPage"));
const ManualPaymentsPage = lazy(() => import("@/pages/superadmin/ManualPaymentsPage"));
const StaffManagementPage = lazy(() => import("@/pages/app/StaffManagementPage"));
const AttendancePage = lazy(() => import("@/pages/app/AttendancePage"));
const WorkspaceEntryPage = lazy(() => import("@/pages/WorkspaceEntryPage"));
const DevicesPage = lazy(() => import("@/pages/app/DevicesPage"));
const PosLoginPage = lazy(() => import("@/pages/pos/PosLoginPage"));
const MoneyPage = lazy(() => import("@/pages/app/MoneyPage"));
const SuppliersPage = lazy(() => import("@/pages/app/SuppliersPage"));
const PurchaseOrdersPage = lazy(() => import("@/pages/app/PurchaseOrdersPage"));
const ReceiveStockPage = lazy(() => import("@/pages/app/ReceiveStockPage"));
const StockTransferPage = lazy(() => import("@/pages/app/StockTransferPage"));
const RecipesPage = lazy(() => import("@/pages/app/RecipesPage"));
const WastePage = lazy(() => import("@/pages/app/WastePage"));
const RestaurantPage = lazy(() => import("@/pages/app/RestaurantPage"));
const PickleballPage = lazy(() => import("@/pages/app/PickleballPage"));
const VillaPage = lazy(() => import("@/pages/app/VillaPage"));
const FeatureRouteGuard = lazy(() => import("@/pages/app/FeatureRouteGuard"));
const OwnerDashboardPage = lazy(() => import("@/pages/app/OwnerDashboardPage"));
const ReportsPage = lazy(() => import("@/pages/app/ReportsPage"));
const PrinterSettingsPage = lazy(() => import("@/pages/app/PrinterSettingsPage"));
const FAQPage = lazy(() => import("@/pages/public/FAQPage"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));
const CoffeeIndustryPage = lazy(() => import("@/pages/public/CoffeeIndustryPage"));
const RetailIndustryPage = lazy(() => import("@/pages/public/RetailIndustryPage"));
const OfflinePOSPage = lazy(() => import("@/pages/public/OfflinePOSPage"));
const HowItWorksPage = lazy(() => import("@/pages/public/HowItWorksPage"));
const SuperAdminCmsPage = lazy(() => import("@/pages/superadmin/CmsPage"));
const SuperAdminAccountPage = lazy(() => import("@/pages/superadmin/SuperAdminAccountPage"));
const SuperAdminDevTestingPage = lazy(() => import("@/pages/superadmin/SuperAdminDevTestingPage"));

// Flatten frontend module pages into a route array
function moduleRoutes() {
  const routes = [];
  Object.entries(frontendModules).forEach(([moduleId, desc]) => {
    Object.entries(desc.pages || {}).forEach(([path, Comp]) => {
      // path like "/app/inventory" -> route path "inventory" (child of /app)
      const rel = path.replace(/^\/app\/?/, "");
      routes.push({ path: rel, element: <Comp />, moduleId });
    });
  });
  return routes;
}

/** Minimal in-app spinner used while a lazy chunk downloads. The initial
 *  cold-load splash is in index.html — this one covers subsequent navigations. */
function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div
        className="h-6 w-6 rounded-full border-2 border-muted-foreground/20 border-t-primary animate-spin"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

function App() {
  const modRoutes = moduleRoutes();
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ModuleProvider>
            <FeatureVisibilityProvider>
            <RootRouter>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<ContactPage />} />
                {/* Public /industries now redirects to the homepage Industry
                    Showcase anchor — see §2 of the Industries Nav Cleanup. We
                    keep the route alive so old links/bookmarks never 404. */}
                <Route path="/industries" element={<Navigate to="/#industries" replace />} />
                <Route path="/industries/coffee" element={<CoffeeIndustryPage />} />
                <Route path="/industries/retail" element={<RetailIndustryPage />} />
                <Route path="/offline-pos" element={<OfflinePOSPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/app" element={<AppShell />}>
                  <Route index element={<DashboardRouter />} />
                  <Route path="onboarding" element={<Navigate to="/app" replace />} />
                  <Route path="marketplace" element={<MarketplaceGuard><MarketplacePage /></MarketplaceGuard>} />
                  <Route path="templates" element={<MarketplaceGuard><TemplatesPage /></MarketplaceGuard>} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="company" element={<CompanySettingsPage />} />
                  <Route path="staff" element={<StaffManagementPage />} />
                  <Route path="attendance" element={<AttendancePage />} />
                  <Route path="devices" element={<DevicesPage />} />
                  <Route path="money" element={<MoneyPage />} />
                  <Route path="suppliers" element={<SuppliersPage />} />
                  <Route path="purchasing" element={<PurchaseOrdersPage />} />
                  <Route path="receive" element={<ReceiveStockPage />} />
                  <Route path="transfers" element={<FeatureRouteGuard featureKey="transfer_stock" label="Transfer Stock"><StockTransferPage /></FeatureRouteGuard>} />
                  <Route path="recipes" element={<FeatureRouteGuard featureKey="recipes" label="Recipes"><RecipesPage /></FeatureRouteGuard>} />
                  <Route path="waste" element={<FeatureRouteGuard featureKey="waste" label="Waste & Spoilage"><WastePage /></FeatureRouteGuard>} />
                  <Route path="restaurant" element={<FeatureRouteGuard featureKey="restaurant_tables" label="Restaurant"><RestaurantPage /></FeatureRouteGuard>} />
                  {/* Phase E — Pickleball hub (route-driven tabs). Guarded by booking_core; nested routes share the same component. */}
                  <Route path="pickleball" element={<FeatureRouteGuard featureKey="pickleball_courts" label="Pickleball"><PickleballPage /></FeatureRouteGuard>} />
                  <Route path="pickleball/calendar" element={<FeatureRouteGuard featureKey="pickleball_courts" label="Pickleball"><PickleballPage /></FeatureRouteGuard>} />
                  <Route path="pickleball/courts" element={<FeatureRouteGuard featureKey="pickleball_courts" label="Courts"><PickleballPage /></FeatureRouteGuard>} />
                  <Route path="pickleball/players" element={<FeatureRouteGuard featureKey="pickleball_players" label="Players"><PickleballPage /></FeatureRouteGuard>} />
                  <Route path="pickleball/memberships" element={<FeatureRouteGuard featureKey="pickleball_memberships" label="Memberships"><PickleballPage /></FeatureRouteGuard>} />
                  <Route path="pickleball/coaching" element={<FeatureRouteGuard featureKey="pickleball_coaching" label="Coaching"><PickleballPage /></FeatureRouteGuard>} />
                  <Route path="pickleball/rentals" element={<FeatureRouteGuard featureKey="pickleball_rentals" label="Rentals"><PickleballPage /></FeatureRouteGuard>} />
                  {/* Phase F — Villa / Short-Stay hub. */}
                  <Route path="villa" element={<FeatureRouteGuard featureKey="short_stay_units" label="Short-Stay"><VillaPage /></FeatureRouteGuard>} />
                  <Route path="villa/calendar" element={<FeatureRouteGuard featureKey="short_stay_reservations" label="Reservations"><VillaPage /></FeatureRouteGuard>} />
                  <Route path="villa/units" element={<FeatureRouteGuard featureKey="short_stay_units" label="Units"><VillaPage /></FeatureRouteGuard>} />
                  <Route path="villa/checkin" element={<FeatureRouteGuard featureKey="short_stay_checkin" label="Check-In / Out"><VillaPage /></FeatureRouteGuard>} />
                  <Route path="villa/reports" element={<FeatureRouteGuard featureKey="short_stay_reports" label="Reports"><VillaPage /></FeatureRouteGuard>} />
                  <Route path="owner" element={<OwnerDashboardPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="printing" element={<PrinterSettingsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="billing/success" element={<BillingSuccessPage />} />
                  {modRoutes.map((r) => (
                    <Route key={r.path} path={r.path} element={r.element} />
                  ))}
                </Route>
                <Route path="/pos/login" element={<PosLoginPage />} />
                <Route path="/w/:slug" element={<WorkspaceEntryPage basePath="/w" />} />
                {/* V3.1-BETA §H — Employee login URL. Same component, same backend,
                    same authorized-device + PIN flow. Different base path so Owner
                    can share a dedicated staff-entry link. */}
                <Route path="/emp/:slug" element={<WorkspaceEntryPage basePath="/emp" />} />
                <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
                <Route path="/super-admin" element={<SuperAdminShell />}>
                  <Route index element={<SuperAdminOverview />} />
                  <Route path="tenants" element={<TenantsListPage />} />
                  <Route path="tenants/:workspaceId" element={<TenantDetailPage />} />
                  <Route path="plans" element={<PlansManagerPage />} />
                  <Route path="modules" element={<ModulesManagerPage />} />
                  <Route path="gateways" element={<GatewaysManagerPage />} />
                  <Route path="manual-payments" element={<ManualPaymentsPage />} />
                  <Route path="coupons" element={<CouponsManagerPage />} />
                  <Route path="transactions" element={<TransactionsPage />} />
                  <Route path="audit" element={<AuditPage />} />
                  <Route path="cms" element={<SuperAdminCmsPage />} />
                  <Route path="account" element={<SuperAdminAccountPage />} />
                  <Route path="dev-testing" element={<SuperAdminDevTestingPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </RootRouter>
            <Toaster position="top-right" richColors closeButton />
            </FeatureVisibilityProvider>
          </ModuleProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
